<template>
    <div class="product-list-island">
        <product-list-show-card
            v-if="payload.mode === 'show'"
            :payload="payload"
            @copied="swalToast($event, 'success')"
        />

        <form v-else novalidate @submit.prevent="onFormSubmit">
            <header class="pl-toolbar">
                <div class="pl-toolbar-title">
                    <div class="pl-title-row">
                        <h1>{{ list.name.trim() || 'Nuova lista' }}</h1>
                        <span class="pl-badge" :class="payload.mode === 'create' ? 'pl-badge--neutral' : 'pl-badge--status'">
                            {{ payload.mode === 'create' ? 'Bozza' : 'Modifica' }}
                        </span>
                    </div>
                    <p>#{{ list.id ?? '—' }} · {{ String(list.url).trim() || '—' }}</p>
                </div>
                <div class="pl-toolbar-actions">
                    <a class="pl-button pl-button--outline" :href="payload.routes.index">Indietro</a>
                    <a
                        v-if="list.preview_url"
                        class="pl-button pl-button--outline"
                        :href="list.preview_url"
                        target="_blank"
                        rel="noopener noreferrer"
                    >Anteprima</a>
                    <button type="submit" class="pl-button pl-button--primary" :disabled="saving || slugStatus === 'checking'">
                        {{ saving || slugStatus === 'checking' ? '…' : 'Salva' }}
                    </button>
                </div>
            </header>

            <div class="pl-content">
                <div v-if="bannerVisible && payload.slug_conflict" role="alert" class="pl-conflict-banner">
                    <span class="pl-conflict-icon" aria-hidden="true">!</span>
                    <p>
                        <strong>Questo slug è usato anche dalla lista «{{ payload.slug_conflict.name }}» (#{{ payload.slug_conflict.id }})</strong>
                        — sul sito viene letta la lista con id più basso.
                        <a :href="payload.slug_conflict.show_url" class="pl-button pl-button--link">Apri</a>
                        <a :href="payload.slug_conflict.edit_url" class="pl-button pl-button--link">Modifica</a>
                    </p>
                    <button type="button" class="pl-button pl-button--ghost pl-dismiss" aria-label="Chiudi avviso" @click="bannerVisible = false">×</button>
                </div>

                <div class="pl-sections">
                    <section class="pl-section">
                        <div class="pl-section-body">
                            <div class="pl-grid pl-grid--2">
                                <name-slug-fields
                                    ref="nameSlugFields"
                                    :mode="payload.mode"
                                    :name="list.name"
                                    :url="list.url"
                                    :name-label="label('name', 'Nome')"
                                    :url-label="label('url', 'Url')"
                                    :name-error="fieldError('name')"
                                    :url-error="fieldError('url')"
                                    :slug-status="slugStatus"
                                    :slug-result="slugResult"
                                    @update:name="list.name = $event"
                                    @update:url="onSlugDerivedFromName"
                                    @check-slug="onNameBlurCheckSlug"
                                    @clear-error="clearError"
                                />
                                <div class="pl-field" :class="{ 'pl-field--error': fieldError('title') }" data-field="title">
                                    <label for="pl-title">{{ label('title', 'Titolo') }} <span class="pl-required">*</span></label>
                                    <input id="pl-title" v-model="list.title" class="pl-control" maxlength="180" @input="clearError('title')">
                                    <p v-if="fieldError('title')" class="pl-field-error">{{ fieldError('title') }}</p>
                                </div>
                                <div class="pl-field" :class="{ 'pl-field--error': fieldError('description') }" data-field="description">
                                    <label for="pl-description">{{ label('description', 'Descrizione') }} <span class="pl-required">*</span></label>
                                    <textarea id="pl-description" v-model="list.description" class="pl-control pl-textarea" rows="3" @input="clearError('description')"></textarea>
                                    <p v-if="fieldError('description')" class="pl-field-error">{{ fieldError('description') }}</p>
                                </div>
                                <div class="pl-field pl-span-2" :class="{ 'pl-field--error': fieldError('ean') }" data-field="ean">
                                    <label for="pl-ean">{{ label('ean', 'Articoli') }}</label>
                                    <textarea id="pl-ean" :value="eanText" class="pl-control pl-textarea" rows="3" @input="onEanInput"></textarea>
                                    <div class="pl-field-meta"><span class="pl-badge pl-badge--outline">{{ eanCount }} EAN</span></div>
                                    <p v-if="fieldError('ean')" class="pl-field-error">{{ fieldError('ean') }}</p>
                                </div>
                                <div class="pl-field pl-span-2" :class="{ 'pl-field--error': fieldError('keyword') }" data-field="keyword">
                                    <label for="pl-keyword">{{ label('keyword', 'Keyword') }}</label>
                                    <input id="pl-keyword" v-model="list.keyword" class="pl-control" maxlength="100" @input="clearError('keyword')">
                                    <p v-if="fieldError('keyword')" class="pl-field-error">{{ fieldError('keyword') }}</p>
                                </div>
                            </div>
                        </div>
                    </section>

                    <section class="pl-section">
                        <div class="pl-section-heading"><h2>Parametri</h2></div>
                        <div class="pl-section-body">
                            <div class="pl-grid pl-grid--3">
                                <remote-multi-select
                                    v-for="field in paramRemoteFieldsRow1"
                                    :key="field"
                                    :field="field"
                                    :label="label(field, field)"
                                    :model-value="selected[field] || []"
                                    :route="payload.routes.search[field] || ''"
                                    :error="fieldError(field)"
                                    @update:model-value="setSelected(field, $event)"
                                />
                            </div>
                            <div class="pl-grid pl-grid--3">
                                <remote-multi-select
                                    v-for="field in paramRemoteFieldsRow2"
                                    :key="field"
                                    :field="field"
                                    :label="label(field, field)"
                                    :model-value="selected[field] || []"
                                    :route="payload.routes.search[field] || ''"
                                    :error="fieldError(field)"
                                    @update:model-value="setSelected(field, $event)"
                                />
                                <div class="pl-field" :class="{ 'pl-field--error': fieldError('orchestra') }" data-field="orchestra">
                                    <label for="pl-orchestra">{{ label('orchestra', 'Interprete') }}</label>
                                    <input id="pl-orchestra" v-model="list.orchestra" class="pl-control" maxlength="200" @input="clearError('orchestra')">
                                    <p v-if="fieldError('orchestra')" class="pl-field-error">{{ fieldError('orchestra') }}</p>
                                </div>
                            </div>
                            <div class="pl-grid pl-grid--3">
                                <remote-multi-select
                                    v-if="showRemoteField('genre')"
                                    field="genre"
                                    :label="label('genre', 'Genere')"
                                    :model-value="selected.genre || []"
                                    :route="payload.routes.search.genre || ''"
                                    :error="fieldError('genre')"
                                    @update:model-value="setSelected('genre', $event)"
                                />
                                <div class="pl-field" data-field="property">
                                    <label>{{ label('property', 'Proprietà') }}</label>
                                    <div class="pl-static-select">
                                        <button type="button" class="pl-button pl-button--outline pl-static-trigger" @click="propertyOpen = !propertyOpen">
                                            <span v-if="list.property.length" class="pl-show-values">
                                                <span v-for="value in list.property" :key="value" class="pl-chip">
                                                    {{ (payload.options.property || {})[value] || value }}
                                                    <button type="button" class="pl-chip-remove" @click.stop="toggleProperty(value)">×</button>
                                                </span>
                                            </span>
                                            <span v-else class="pl-placeholder">Seleziona…</span>
                                            <span class="pl-static-chevron">▾</span>
                                        </button>
                                        <div v-if="propertyOpen" class="pl-results">
                                            <label
                                                v-for="(text, value, index) in payload.options.property || {}"
                                                :key="value"
                                                class="pl-result-row pl-checkbox-row"
                                                :class="{ 'pl-result-row--highlighted': index === 0 }"
                                            >
                                                <input type="checkbox" :checked="list.property.includes(value)" @change="toggleProperty(value)">
                                                {{ text }}
                                            </label>
                                        </div>
                                    </div>
                                </div>
                                <remote-multi-select
                                    v-if="showRemoteField('mood')"
                                    field="mood"
                                    :label="label('mood', 'Mood')"
                                    :model-value="selected.mood || []"
                                    :route="payload.routes.search.mood || ''"
                                    :error="fieldError('mood')"
                                    @update:model-value="setSelected('mood', $event)"
                                />
                            </div>
                        </div>
                    </section>

                    <section class="pl-section">
                        <div class="pl-section-heading"><h2>Prezzo</h2></div>
                        <div class="pl-section-body">
                            <div class="pl-grid pl-grid--2">
                                <div class="pl-field" :class="{ 'pl-field--error': fieldError('min') }" data-field="min">
                                    <label for="pl-min">{{ label('min', 'Minimo') }}</label>
                                    <input id="pl-min" v-model="list.min" class="pl-control" type="number" min="0" step="0.01">
                                    <p v-if="fieldError('min')" class="pl-field-error">{{ fieldError('min') }}</p>
                                </div>
                                <div class="pl-field" :class="{ 'pl-field--error': fieldError('max') }" data-field="max">
                                    <label for="pl-max">{{ label('max', 'Massimo') }}</label>
                                    <input id="pl-max" v-model="list.max" class="pl-control" type="number" min="0" step="0.01">
                                    <p v-if="fieldError('max')" class="pl-field-error">{{ fieldError('max') }}</p>
                                </div>
                            </div>
                        </div>
                    </section>

                    <section class="pl-section">
                        <div class="pl-section-heading"><h2>Filtri</h2></div>
                        <div class="pl-section-body">
                            <div class="pl-grid pl-grid--3 pl-filtri-grid">
                                <div class="pl-filtri-col">
                                    <div class="pl-field" :class="{ 'pl-field--error': fieldError('days') }" data-field="days">
                                        <label for="pl-days">{{ label('days', 'Giorni/Settimana') }}</label>
                                        <input id="pl-days" v-model="list.days" class="pl-control" type="number" min="0">
                                        <p v-if="fieldError('days')" class="pl-field-error">{{ fieldError('days') }}</p>
                                    </div>
                                    <label class="pl-switch-label pl-filtri-secondary">
                                        <button type="button" role="switch" class="pl-switch" :aria-checked="list.offer" @click="list.offer = !list.offer"></button>
                                        {{ label('offer', 'Offerta') }}
                                        <span v-if="list.offer" class="pl-badge pl-badge--status">Attivo</span>
                                    </label>
                                </div>
                                <div class="pl-filtri-col">
                                    <div class="pl-field" :class="{ 'pl-field--error': fieldError('time') }" data-field="time">
                                        <label for="pl-time">{{ label('time', 'Intervallo') }}</label>
                                        <div class="pl-select-wrap">
                                            <select id="pl-time" v-model="list.time" class="pl-control pl-select">
                                                <option :value="null">Nessuno</option>
                                                <option v-for="(text, value) in payload.options.time || {}" :key="value" :value="value">{{ text }}</option>
                                            </select>
                                            <span class="pl-select-chevron" aria-hidden="true">▾</span>
                                        </div>
                                        <p v-if="fieldError('time')" class="pl-field-error">{{ fieldError('time') }}</p>
                                    </div>
                                    <label class="pl-switch-label pl-filtri-secondary">
                                        <button type="button" role="switch" class="pl-switch" :aria-checked="list.available" @click="list.available = !list.available"></button>
                                        {{ label('available', 'Disponibile') }}
                                        <span v-if="list.available" class="pl-badge pl-badge--status">Attivo</span>
                                    </label>
                                </div>
                                <div class="pl-filtri-col">
                                    <div class="pl-field" :class="{ 'pl-field--error': fieldError('limits') }" data-field="limits">
                                        <label for="pl-limits">{{ label('limits', 'Risultati') }}</label>
                                        <input id="pl-limits" v-model.number="list.limits" class="pl-control" type="number" min="0">
                                        <p v-if="fieldError('limits')" class="pl-field-error">{{ fieldError('limits') }}</p>
                                    </div>
                                    <div class="pl-field pl-filtri-secondary" :class="{ 'pl-field--error': fieldError('sort') }" data-field="sort">
                                        <label for="pl-sort">{{ label('sort', 'Ordina') }} <span class="pl-required">*</span></label>
                                        <div class="pl-select-wrap">
                                            <select id="pl-sort" v-model="list.sort" class="pl-control pl-select" @change="clearError('sort')">
                                                <option v-for="(text, value) in payload.options.sort || {}" :key="value" :value="value">{{ text }}</option>
                                            </select>
                                            <span class="pl-select-chevron" aria-hidden="true">▾</span>
                                        </div>
                                        <p v-if="fieldError('sort')" class="pl-field-error">{{ fieldError('sort') }}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>

                    <section v-if="showRemoteField('category')" class="pl-section">
                        <div class="pl-section-heading"><h2>Categorie</h2></div>
                        <div class="pl-section-body">
                            <remote-multi-select
                                field="category"
                                :label="label('category', 'Categorie')"
                                :model-value="selected.category || []"
                                :route="payload.routes.search.category || ''"
                                :error="fieldError('category')"
                                @update:model-value="setSelected('category', $event)"
                            />
                        </div>
                    </section>
                </div>
            </div>

        </form>
    </div>
</template>

<script>
import axios from 'axios';
import { normalizeProductListPayload, catalogRemoteFieldsFor, REMOTE_FIELDS } from './normalizeProductListPayload';
import RemoteMultiSelect from './components/RemoteMultiSelect.vue';
import NameSlugFields from './components/NameSlugFields.vue';
import ProductListShowCard from './components/ProductListShowCard.vue';
import './island-styles.css';

export default {
    name: 'ProductListIsland',
    components: {
        RemoteMultiSelect,
        NameSlugFields,
        ProductListShowCard,
    },
    props: {
        initialPayload: { type: Object, required: true },
    },
    data() {
        const payload = normalizeProductListPayload(this.initialPayload);

        return {
            payload,
            list: { ...payload.list },
            selected: { ...payload.selected },
            errors: {},
            slugStatus: 'idle',
            slugResult: payload.slug_conflict,
            bannerVisible: Boolean(payload.mode === 'edit' && payload.slug_conflict),
            saving: false,
            propertyOpen: false,
            applyToExistingConfirmed: false,
            initialSlug: String(payload.list.url),
            checkedSlugKey: '',
        };
    },
    computed: {
        catalogRemoteFields() {
            return catalogRemoteFieldsFor(this.payload);
        },
        paramRemoteFieldsRow1() {
            return ['primaryArtist', 'secondaryArtist', 'label'].filter((field) => this.showRemoteField(field));
        },
        paramRemoteFieldsRow2() {
            return ['supplier', 'campaign'].filter((field) => this.showRemoteField(field));
        },
        eanText() {
            return (this.list.ean || []).join('\n');
        },
        eanCount() {
            return (this.list.ean || []).filter(Boolean).length;
        },
    },
    methods: {
        showRemoteField(field) {
            return this.catalogRemoteFields.includes(field);
        },
        label(field, fallback) {
            return this.payload.labels[field] || fallback;
        },
        fieldError(field) {
            const messages = this.errors[field];

            return Array.isArray(messages) ? messages[0] : null;
        },
        clearError(field) {
            if (this.errors[field]) {
                const next = { ...this.errors };
                delete next[field];
                this.errors = next;
            }
        },
        trimValue(value) {
            return String(value ?? '').trim();
        },
        validateRequiredFields() {
            const errors = {};

            if (this.trimValue(this.list.name) === '') {
                errors.name = ['Inserire Nome'];
            }
            if (this.trimValue(this.list.url) === '') {
                errors.url = ['Inserire Url'];
            }
            if (this.trimValue(this.list.title) === '') {
                errors.title = ['Inserire Titolo'];
            }
            if (this.trimValue(this.list.description) === '') {
                errors.description = ['Inserire Descrizione'];
            }
            if (this.trimValue(this.list.sort) === '') {
                errors.sort = ['Inserire Ordina'];
            }

            return errors;
        },
        focusFirstError(field) {
            if (!field) {
                return;
            }

            this.$nextTick(() => {
                document.querySelector(`[data-field="${field}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            });
        },
        swalToast(title, icon = 'success') {
            const toast = window.SwalToast || window.Swal;
            if (!toast) {
                return;
            }

            toast.fire({ icon, title });
        },
        onSlugDerivedFromName(url) {
            this.list.url = url;
            this.checkedSlugKey = '';
            this.slugStatus = 'idle';
            this.applyToExistingConfirmed = false;
        },
        async onNameBlurCheckSlug() {
            await this.checkSlug();
        },
        onFormSubmit() {
            return this.submit(false);
        },
        swalButtons() {
            return {
                buttonsStyling: false,
                showCancelButton: true,
                cancelButtonText: 'Annulla',
                confirmButtonText: 'OK',
                customClass: {
                    confirmButton: 'btn btn-primary',
                    cancelButton: 'btn btn-default',
                },
            };
        },
        swalConfirmed(result) {
            return Boolean(result && (result.isConfirmed || result.value));
        },
        async promptApplyToExisting() {
            if (!window.Swal || !this.slugResult) {
                return;
            }

            const first = await window.Swal.fire({
                ...this.swalButtons(),
                icon: 'warning',
                text: `Esiste già una lista «${this.slugResult.name}» (#${this.slugResult.id}). Modificarla?`,
            });

            if (!this.swalConfirmed(first)) {
                this.$refs.nameSlugFields?.focusName();
                return;
            }

            const second = await window.Swal.fire({
                ...this.swalButtons(),
                icon: 'warning',
                text: 'Sei sicuro?',
            });

            if (!this.swalConfirmed(second)) {
                return;
            }

            this.applyToExistingConfirmed = true;
            await this.submit(true);
        },
        onEanInput(event) {
            this.list.ean = String(event.target.value).split(/\r?\n/).map((item) => item.trim()).filter(Boolean);
            this.clearError('ean');
        },
        setSelected(field, items) {
            this.selected = { ...this.selected, [field]: items };
            this.clearError(field);
        },
        toggleProperty(value) {
            const current = this.list.property || [];
            this.list.property = current.includes(value)
                ? current.filter((item) => item !== value)
                : [...current, value];
        },
        csrfToken() {
            return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
        },
        async checkSlug() {
            const url = String(this.list.url).trim();
            const checkKey = url;
            if (this.checkedSlugKey === checkKey && (this.slugStatus === 'available' || this.slugStatus === 'conflict')) {
                return;
            }
            if (!url) {
                this.slugStatus = 'idle';
                return;
            }
            if (this.payload.mode === 'edit') {
                this.slugStatus = 'idle';
                return;
            }
            this.slugStatus = 'checking';
            try {
                const { data } = await axios.get(this.payload.routes.slug_check, {
                    params: {
                        url,
                        exclude_id: this.list.id || undefined,
                    },
                    headers: { Accept: 'application/json' },
                });
                this.checkedSlugKey = checkKey;
                this.slugResult = data.exists ? data.list : null;
                this.slugStatus = data.exists ? 'conflict' : 'available';
            } catch (error) {
                this.slugStatus = 'idle';
            }
        },
        buildBody(applyToExisting = false) {
            const body = {
                name: this.list.name,
                url: this.list.url,
                title: this.list.title,
                description: this.list.description,
                ean: (this.list.ean || []).join('\r\n'),
                keyword: this.list.keyword,
                orchestra: this.list.orchestra,
                days: this.list.days === '' || this.list.days === null ? null : Number(this.list.days),
                time: this.list.time || null,
                offer: Boolean(this.list.offer),
                available: Boolean(this.list.available),
                sort: this.list.sort,
                limits: this.list.limits === '' || this.list.limits === null ? null : Number(this.list.limits),
                min: this.list.min,
                max: this.list.max,
                property: this.list.property || [],
                _pl_ajax: {},
            };

            if (applyToExisting && this.payload.mode === 'create') {
                body.apply_to_existing = true;
            }

            REMOTE_FIELDS.forEach((field) => {
                body[field] = (this.selected[field] || []).map((item) => item.value);
                body._pl_ajax[field] = 1;
            });

            return body;
        },
        async submit(forceApplyToExisting = false) {
            if (this.slugStatus === 'checking') {
                return;
            }

            const applyToExisting = forceApplyToExisting === true || this.applyToExistingConfirmed;

            if (this.payload.mode === 'create' && !applyToExisting) {
                await this.checkSlug();
            }

            if (
                this.payload.mode === 'create'
                && this.slugStatus === 'conflict'
                && this.slugResult
                && !applyToExisting
            ) {
                await this.promptApplyToExisting();
                return;
            }

            const clientErrors = this.validateRequiredFields();
            if (Object.keys(clientErrors).length > 0) {
                this.errors = clientErrors;
                this.focusFirstError(Object.keys(clientErrors)[0]);
                return;
            }

            this.saving = true;
            const url = this.payload.mode === 'create' ? this.payload.routes.store : this.payload.routes.update;
            const method = this.payload.mode === 'create' ? 'post' : 'put';

            try {
                const { data } = await axios({
                    url,
                    method,
                    data: this.buildBody(applyToExisting),
                    headers: {
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                        'X-CSRF-TOKEN': this.csrfToken(),
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                });
                this.swalToast(data.message || 'Salvato', 'success');
                if (data.redirect) {
                    window.setTimeout(() => {
                        window.location.assign(data.redirect);
                    }, 400);
                }
            } catch (error) {
                const response = error.response;
                if (response?.status === 422) {
                    this.errors = response.data.errors || {};
                    const slugError = (this.errors.url || []).some((message) => String(message).toLocaleLowerCase('it').includes('slug'));
                    const existing = this.slugResult || this.payload.slug_conflict;
                    if (slugError && existing) {
                        this.slugResult = existing;
                        this.slugStatus = 'conflict';
                        this.saving = false;
                        await this.promptApplyToExisting();
                        return;
                    }
                    this.focusFirstError(Object.keys(this.errors)[0]);
                } else {
                    this.swalToast('Salvataggio non riuscito.', 'error');
                }
            } finally {
                this.saving = false;
            }
        },
    },
};
</script>
